import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { HOMOLOGY_CLASSES, type HomologyClass } from '@peruselab/structure-editor/domain/markushDocument';
import {
  addRGroupMember,
  removeRGroupDefinition,
  removeRGroupMember,
  setRGroupDefinition
} from '@peruselab/structure-editor/domain/markushMutations';
import { fragmentKey, fragmentLabel } from '@peruselab/structure-editor/domain/markushSummary';
import { COMMON_SUBSTITUENTS, fragmentFromSmiles } from '@peruselab/structure-editor/chemistry/markushSubstituents';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import { Icon } from '../../components/Icon';

// #839 -- each palette chip's fragment key (the palette is fixed), worked out once per session from
// its SMILES so a chip already in an R-group's list can show as added.
let paletteKeys: Promise<ReadonlyMap<string, string>> | null = null;
function loadPaletteKeys(): Promise<ReadonlyMap<string, string>> {
  paletteKeys ??= Promise.all(
    COMMON_SUBSTITUENTS.map(async ({ key, smiles }) => [key, fragmentKey(await fragmentFromSmiles(smiles))] as const)
  ).then((entries) => new Map(entries));
  return paletteKeys;
}

function usePaletteKeys(): ReadonlyMap<string, string> | null {
  const [keys, setKeys] = useState<ReadonlyMap<string, string> | null>(null);
  useEffect(() => {
    let current = true;
    void loadPaletteKeys()
      .then((loaded) => {
        if (current) setKeys(loaded);
      })
      .catch(() => {
        paletteKeys = null;
      });
    return () => {
      current = false;
    };
  }, []);
  return keys;
}

export interface RGroupRevealRequest {
  readonly label: string;
  readonly sequence: number;
}

interface MarkushSearchSectionProps {
  readonly revealRequest?: RGroupRevealRequest | undefined;
  readonly doc: UseDrawingDocumentResult;
}

type Mode = 'list' | 'class';

// #672 -- everything about asking a Markush question, as one section of the Filter tab (ADR 0013:
// the Markush drawing is the search query): the structure count and the substituent table (#652)
// -- what each R label stands for, an explicit member list (bounded; palette chips + SMILES whose
// first atom attaches) or a generic class (unbounded). Every R-group change goes through
// doc.editRGroups, so it is one undo step and rides along with the drawing through export/import.
// #674: shown only while the drawing has Markush content -- there is no mode to start or leave;
// the drawing tools (R label, atom list, repeat unit) on the tool rail are the way in.
export function MarkushSearchSection({ doc, revealRequest }: MarkushSearchSectionProps) {
  const { t } = useTranslation();
  return (
    <section
      className="rail-section markush-search-section"
      aria-labelledby="markush-search-heading"
      data-testid="markush-search-section"
    >
      <h2 id="markush-search-heading">{t('markushSearchSection.heading')}</h2>
      <RGroupTable doc={doc} revealRequest={revealRequest} />
    </section>
  );
}

function RGroupTable({ doc, revealRequest }: MarkushSearchSectionProps) {
  const { t } = useTranslation();
  const count = doc.markushStructureCount;
  const undefinedLabels = doc.markushUsedLabels.filter((label) => !doc.markushLayers.rGroups[label]);
  const countText =
    count === null || count.kind === 'incomplete'
      ? t('rgroupsPanel.countIncomplete', { labels: undefinedLabels.join(', ') })
      : count.kind === 'generic'
        ? t('rgroupsPanel.countGeneric')
        : t('rgroupsPanel.count', { count: count.value });

  return (
    <div className="rgroups-panel" data-testid="rgroups-panel">
      <p className="rgroups-count" data-testid="rgroups-count">
        {countText}
      </p>
      {doc.markushTableLabels.length === 0 ? (
        <p className="rgroups-empty">{t('rgroupsPanel.empty')}</p>
      ) : (
        doc.markushTableLabels.map((label) => (
          <RGroupSection
            key={label}
            doc={doc}
            label={label}
            revealRequest={revealRequest?.label === label ? revealRequest : undefined}
          />
        ))
      )}
    </div>
  );
}

function RGroupSection({ doc, label, revealRequest }: MarkushSearchSectionProps & { readonly label: string }) {
  const { t } = useTranslation();
  const definition = doc.markushLayers.rGroups[label];
  // A defined label's mode follows its definition (so undo and import keep it right); the local
  // choice only matters while the label is undefined.
  const [chosenMode, setChosenMode] = useState<Mode>('list');
  const mode: Mode = definition ? (definition.kind === 'homology' ? 'class' : 'list') : chosenMode;
  const [smiles, setSmiles] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Set when the SMILES box holds a class name ("Alkyl") -- offered as a one-tap switch.
  const [suggestedClass, setSuggestedClass] = useState<HomologyClass | null>(null);
  const [adding, setAdding] = useState(false);
  // #672: an undefined label starts open (it needs attention); a defined one starts as its summary.
  const [disclosure, setDisclosure] = useState({
    open: definition === undefined || revealRequest !== undefined,
    request: revealRequest
  });
  // A new placement reopens this label even if the user collapsed it earlier.
  if (revealRequest && disclosure.request !== revealRequest) {
    setDisclosure({ open: true, request: revealRequest });
  }
  const sectionRef = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (!revealRequest) return;
    // Wait for expansion before scrolling within the rail; keep focus on the drawing tool.
    const frame = requestAnimationFrame(() => sectionRef.current?.scrollIntoView({ block: 'nearest' }));
    return () => cancelAnimationFrame(frame);
  }, [revealRequest]);

  // An async SMILES parse must apply to the drawing as it is *then*, not as it was on submit.
  const latestDoc = useRef(doc);
  useEffect(() => {
    latestDoc.current = doc;
  }, [doc]);

  const members = definition?.kind === 'enumerated' ? definition.members : [];
  const keyOfChip = usePaletteKeys();
  const memberKeys = new Set(members.map(fragmentKey));
  const status =
    definition === undefined
      ? t('rgroupsPanel.notDefined')
      : definition.kind === 'homology'
        ? t(`rgroupsPanel.classes.${definition.class}`)
        : t('rgroupsPanel.memberCount', { count: members.length });

  const addSmiles = async (input: string) => {
    setError(null);
    setSuggestedClass(null);
    setAdding(true);
    try {
      const fragment = await fragmentFromSmiles(input);
      latestDoc.current.editRGroups((markush) => addRGroupMember(markush, label, fragment));
      return true;
    } catch {
      // RDKit's own message ("could not parse or sanitize") doesn't say what the box expects.
      setError(t('rgroupsPanel.smilesError', { input: input.trim() }));
      return false;
    } finally {
      setAdding(false);
    }
  };

  /** A generic class typed into the SMILES box -- "Alkyl", "aryl", ... in any case. */
  const classNamed = (input: string): HomologyClass | undefined => {
    const typed = input.trim().toLowerCase();
    return HOMOLOGY_CLASSES.find(
      (homologyClass) => homologyClass === typed || t(`rgroupsPanel.classes.${homologyClass}`).toLowerCase() === typed
    );
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const homologyClass = classNamed(smiles);
    if (homologyClass) {
      setSuggestedClass(homologyClass);
      setError(t('rgroupsPanel.looksLikeClass', { input: smiles.trim() }));
      return;
    }
    if (await addSmiles(smiles)) setSmiles('');
  };

  /** One step from the list to a class (replacing any members, after asking). */
  const applyClass = (homologyClass: HomologyClass) => {
    if (members.length > 0 && !globalThis.confirm(t('rgroupsPanel.confirmDropList', { label }))) return;
    doc.editRGroups((markush) => setRGroupDefinition(markush, label, { kind: 'homology', class: homologyClass }));
    setSmiles('');
    setError(null);
    setSuggestedClass(null);
  };

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    if (next === 'class' && members.length > 0 && !globalThis.confirm(t('rgroupsPanel.confirmDropList', { label }))) {
      return;
    }
    // A list and a class can't coexist: switching drops the other kind's definition.
    if (definition) doc.editRGroups((markush) => removeRGroupDefinition(markush, label));
    setChosenMode(next);
  };

  return (
    <details
      ref={sectionRef}
      className="rgroup-section"
      aria-label={t('rgroupsPanel.sectionLabel', { label })}
      data-testid={`rgroup-${label}`}
      open={disclosure.open}
      onToggle={(event) => {
        const open = event.currentTarget.open;
        setDisclosure((current) => ({ ...current, open }));
      }}
    >
      <summary>
        <h3 className="rgroup-heading">
          <span className="r-group-glyph">{label}</span>
          <span className={definition ? 'rgroup-status' : 'rgroup-status rgroup-status--undefined'}>{status}</span>
        </h3>
        <Icon name="expandMore" className="rgroup-chevron" />
      </summary>

      <div className="rgroup-mode" role="group" aria-label={t('rgroupsPanel.kind')}>
        {(['list', 'class'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={mode === value ? 'active' : ''}
            aria-pressed={mode === value}
            onClick={() => switchMode(value)}
          >
            {t(`rgroupsPanel.mode.${value}`)}
          </button>
        ))}
      </div>

      {mode === 'class' ? (
        <label className="rgroup-class">
          <span>{t('rgroupsPanel.classLabel')}</span>
          <select
            data-testid={`rgroup-${label}-class`}
            value={definition?.kind === 'homology' ? definition.class : ''}
            onChange={(event) => {
              const chosen = event.target.value as HomologyClass;
              doc.editRGroups((markush) => setRGroupDefinition(markush, label, { kind: 'homology', class: chosen }));
            }}
          >
            <option value="" disabled>
              {t('rgroupsPanel.chooseClass')}
            </option>
            {HOMOLOGY_CLASSES.map((homologyClass) => (
              <option key={homologyClass} value={homologyClass}>
                {t(`rgroupsPanel.classes.${homologyClass}`)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <>
          {members.length > 0 ? (
            <ul className="rgroup-members" aria-label={t('rgroupsPanel.members', { label })}>
              {members.map((member, index) => {
                const text = fragmentLabel(member);
                return (
                  <li key={index} className="rgroup-chip">
                    <span>{text}</span>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={t('rgroupsPanel.removeMember', { member: text, label })}
                      title={t('rgroupsPanel.removeMember', { member: text, label })}
                      onClick={() => doc.editRGroups((markush) => removeRGroupMember(markush, label, index))}
                    >
                      <Icon name="close" size="1rem" />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}

          <div className="rgroup-palette" role="group" aria-label={t('rgroupsPanel.palette')}>
            {COMMON_SUBSTITUENTS.map(({ key, smiles: paletteSmiles, label: chipText }) => {
              // #839 -- a substituent already in this R-group's list can't be added again.
              const chipKey = keyOfChip?.get(key);
              const added = chipKey !== undefined && memberKeys.has(chipKey);
              const name = added
                ? t('rgroupsPanel.memberAdded', { member: chipText, label })
                : t('rgroupsPanel.addMember', { member: chipText, label });
              return (
                <button
                  key={key}
                  type="button"
                  className={`rgroup-palette-chip${added ? ' rgroup-palette-chip--added' : ''}`}
                  data-testid={`rgroup-${label}-add-${key}`}
                  aria-label={name}
                  title={name}
                  disabled={adding || added}
                  onClick={() => void addSmiles(paletteSmiles)}
                >
                  {chipText}
                </button>
              );
            })}
          </div>

          <form className="rgroup-smiles" onSubmit={(event) => void submit(event)}>
            <input
              type="text"
              value={smiles}
              data-testid={`rgroup-${label}-smiles`}
              aria-label={t('rgroupsPanel.smilesLabel', { label })}
              placeholder={t('rgroupsPanel.smilesPlaceholder')}
              onChange={(event) => setSmiles(event.target.value)}
            />
            <button
              type="submit"
              className="icon-button"
              disabled={adding || smiles.trim() === ''}
              aria-label={t('rgroupsPanel.addSmiles', { label })}
              title={t('rgroupsPanel.addSmiles', { label })}
            >
              <Icon name="add" />
            </button>
          </form>
          {error ? (
            <div className="rgroup-error" role="alert">
              <p>{error}</p>
              {suggestedClass ? (
                <button
                  type="button"
                  data-testid={`rgroup-${label}-use-class`}
                  onClick={() => applyClass(suggestedClass)}
                >
                  {t('rgroupsPanel.useClass', { label, className: t(`rgroupsPanel.classes.${suggestedClass}`) })}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </details>
  );
}
